// material
import { Card, CardHeader, Box } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import ModernTable from '../components/ModernTable';
import { useTheme } from '@mui/material/styles';
import { Typography, Chip, alpha } from '@mui/material';
import Paper from '@mui/material/Paper';
import { CloudDownload } from '@mui/icons-material';
import Swal from 'sweetalert2';
import { Email } from '@mui/icons-material';
import request from '../../../services/Request';
import moment from 'moment';
import { ExportToCsv } from 'export-to-csv';
import { useState, useEffect } from 'react';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ComputerIcon from '@mui/icons-material/Computer';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import EmailIcon from '@mui/icons-material/Email';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { currencies } from '../../../staticData/currencies';

const getCurrencySymbol = (code) => currencies.find((c) => c.code === code)?.symbol || '$';

export default function ReportTable(props) {
  const theme = useTheme();
  const { order, startDate, endDate, machinesName, id } = props;

  const [pagination, setPagination] = useState(
    localStorage.getItem('table_view_report1') ? localStorage.getItem('table_view_report1') : 10
  );
  const [filteredData, setFilteredData] = useState([]); // State to hold filtered data

  useEffect(() => {
    localStorage.setItem('table_view_report1', pagination);
  }, [pagination]);

  const columns = [
    {
      title: 'Order ID',
      field: 'id',
      render: (rowData) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
          {rowData.id}
        </Typography>
      ),
    },
    {
      title: 'Order Date',
      field: 'createdAt',
      render: (rowData) => {
        let date = new Date(rowData.createdAt);
        //show keep old trans date
        if (rowData.realTime) {
          date = moment.utc(date).format('lll');
        } else {
          date = moment(date).format('lll');
        }
        return (
          <Typography variant="body2">{date}</Typography>
        );
      },
    },
    {
      title: 'Machine ID',
      field: 'machineInfo.name',
      render: (rowData) => (
        <Chip
          label={rowData.machineInfo?.name}
          size="small"
          sx={{
            fontWeight: 600,
            backgroundColor: alpha(theme.palette.info.main, 0.12),
            color: theme.palette.info.dark,
            border: `1.5px solid ${alpha(theme.palette.info.main, 0.3)}`,
          }}
        />
      ),
    },
    {
      title: 'Machine Desc',
      field: 'machineInfo.description',
      render: (rowData) => (
        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
          {rowData.machineInfo?.description}
        </Typography>
      ),
    },
    {
      title: 'Currency',
      field: 'currency',
      render: (rowData) => (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {rowData.currency}
        </Typography>
      ),
    },
    {
      title: 'Tax',
      field: 'tax',
      render: (rowData) => (
        <Chip
          label={`${getCurrencySymbol(rowData.currency)}${rowData.tax?.toFixed(2)}`}
          size="small"
          sx={{
            fontWeight: 600,
            backgroundColor: alpha(theme.palette.warning.main, 0.12),
            color: theme.palette.warning.dark,
            border: `1.5px solid ${alpha(theme.palette.warning.main, 0.3)}`,
          }}
        />
      ),
    },
    {
      title: 'Sale Amount',
      field: 'total',
      render: (rowData) => (
        <Chip
          label={`${getCurrencySymbol(rowData.currency)}${rowData.total?.toFixed(2)}`}
          size="small"
          sx={{
            fontWeight: 600,
            backgroundColor: alpha(theme.palette.success.main, 0.12),
            color: theme.palette.success.dark,
            border: `1.5px solid ${alpha(theme.palette.success.main, 0.3)}`,
          }}
        />
      ),
    },
    {
      title: 'Email Receipt',
      field: 'email',
      render: (rowData) => {
        if (rowData.email) {
          return (
            <Chip
              icon={<EmailIcon sx={{ fontSize: 16 }} />}
              label={rowData.email}
              size="small"
              sx={{
                fontWeight: 600,
                backgroundColor: rowData.isCheck
                  ? alpha(theme.palette.primary.main, 0.12)
                  : alpha(theme.palette.grey[500], 0.12),
                color: rowData.isCheck ? theme.palette.primary.dark : theme.palette.text.secondary,
                border: rowData.isCheck
                  ? `1.5px solid ${alpha(theme.palette.primary.main, 0.3)}`
                  : `1.5px solid ${alpha(theme.palette.grey[500], 0.3)}`,
                '& .MuiChip-icon': {
                  color: rowData.isCheck ? theme.palette.primary.dark : theme.palette.text.secondary,
                },
              }}
            />
          );
        } else {
          return null;
        }
      },
    },
    {
      title: 'Code Used',
      field: 'otc',
      render: (rowData) =>
        rowData.otc ? (
          <Chip
            icon={<LocalOfferIcon sx={{ fontSize: 16 }} />}
            label={rowData.otc}
            size="small"
            sx={{
              fontWeight: 600,
              backgroundColor: alpha(theme.palette.secondary.main, 0.12),
              color: theme.palette.secondary.dark,
              border: `1.5px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
              '& .MuiChip-icon': {
                color: theme.palette.secondary.dark,
              },
            }}
          />
        ) : null,
    },
    {
      title: 'Payment Type',
      field: 'payment_type',
      render: (rowData) =>
        rowData.payment_type ? (
          <Chip
            icon={<CreditCardIcon sx={{ fontSize: 16 }} />}
            label={rowData.payment_type}
            size="small"
            sx={{
              fontWeight: 600,
              backgroundColor: alpha(theme.palette.info.main, 0.12),
              color: theme.palette.info.dark,
              border: `1.5px solid ${alpha(theme.palette.info.main, 0.3)}`,
              '& .MuiChip-icon': {
                color: theme.palette.info.dark,
              },
            }}
          />
        ) : null,
    },
    {
      title: 'EXID',
      field: 'custom',
      render: (rowData) => (
        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
          {rowData.clientUser?.email || rowData.custom || ''}
        </Typography>
      ),
    },
    {
      title: 'Status',
      field: 'status',
      render: (rowData) => (
        <Chip
          icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
          label={rowData.status}
          size="small"
          sx={{
            fontWeight: 600,
            backgroundColor:
              rowData.status === 'Completed'
                ? alpha(theme.palette.success.main, 0.12)
                : alpha(theme.palette.error.main, 0.12),
            color:
              rowData.status === 'Completed'
                ? theme.palette.success.dark
                : theme.palette.error.dark,
            border:
              rowData.status === 'Completed'
                ? `1.5px solid ${alpha(theme.palette.success.main, 0.3)}`
                : `1.5px solid ${alpha(theme.palette.error.main, 0.3)}`,
            '& .MuiChip-icon': {
              color:
                rowData.status === 'Completed'
                  ? theme.palette.success.dark
                  : theme.palette.error.dark,
            },
          }}
        />
      ),
    },
  ];

  const MyNewTitle = ({ text = 'Table Title', variant = 'h6' }) => (
    <Typography variant={variant} style={{ overflow: 'hidden', textOverflow: 'ellipsis', marginTop: '20px' }}>
      {`Transaction Table (${machinesName} from ${startDate.toShortFormat()} to  ${endDate.toShortFormat()})`}
    </Typography>
  );

  const handleSearchChange = (searchText) => {
    // Perform filtering operation on data based on searchText
    const filteredData = order.filter((item) =>
      Object.values(item).some((value) =>
        value?.toString()?.toLowerCase()?.includes(searchText?.toLowerCase().trim())
      )
    );
    // Update filteredData state
    console.log('filteredData', filteredData);
    setFilteredData(filteredData);
  };

  return (
    <ModernTable
      onRowsPerPageChange={setPagination}
      columns={columns}
      data={order}
      onSearchChange={handleSearchChange}
      title={<MyNewTitle variant="h6" />}
      detailPanel={({ rowData }) => {
        // console.log(rowData);
        return (
          <TableContainer
            component={Paper}
            style={{
              backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : '#EBEBE8',
            }}
          >
            <Table aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell>Product Name</TableCell>
                  <TableCell>UPC/SKU</TableCell>
                  <TableCell align="right">Discount</TableCell>
                  <TableCell align="right">Promotion Used</TableCell>
                  <TableCell align="right">Code Sent</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell align="right">Cost Price(unit)</TableCell>
                  <TableCell align="right">Cost Price(total)</TableCell>
                  <TableCell align="right">Unit Price</TableCell>
                  <TableCell align="right">Sub Total without Tax</TableCell>
                  <TableCell align="right">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rowData.products.map(
                  (prod, i) =>
                    prod.productInfo && (
                      <TableRow key={i} style={prod.err ? { backgroundColor: '#fea7a5' } : {}}>
                        <TableCell component="th" scope="row">
                          {prod.productInfo.name}
                        </TableCell>
                        <TableCell component="th" scope="row">
                          {prod.productInfo.upc}
                        </TableCell>
                        <TableCell align="right">{'' + prod.discount.toFixed(2)}</TableCell>
                        {prod.promoType ? (
                          <TableCell align="right">
                            {prod.codeEntered} ({prod.promoType})
                          </TableCell>
                        ) : prod.promoInfo ? (
                          <TableCell align="right">
                            {prod.promoInfo.title} ({prod.promoInfo.promoType})
                          </TableCell>
                        ) : (
                          <TableCell align="right">No Promotion</TableCell>
                        )}
                        {prod.code ? (
                          <TableCell align="right">{prod.code}</TableCell>
                        ) : (
                          <TableCell align="right">No Code</TableCell>
                        )}
                        <TableCell align="right">{prod.quantity}</TableCell>
                        <TableCell align="right">{prod.productInfo?.costPrice}</TableCell>
                        <TableCell align="right">
                          {(prod.productInfo?.costPrice * prod.quantity).toFixed(2)}
                        </TableCell>
                        <TableCell align="right">
                          {(prod.soldPrice / prod.quantity).toFixed(2)}
                        </TableCell>
                        <TableCell align="right">{prod.soldPrice.toFixed(2)}</TableCell>
                        <TableCell align="right">
                          {prod.refund_details?.status || prod.status}
                        </TableCell>
                      </TableRow>
                    )
                )}{' '}
                {rowData.products?.length > 0 &&
                  rowData.products[0].bundle_details?.length > 0 &&
                  rowData.products[0].bundle_details.map((prod, i) => (
                    <TableRow key={i} style={prod.err ? { backgroundColor: '#fea7a5' } : {}}>
                      <TableCell component="th" scope="row">
                        {prod.name + ' (Bundle Item)'}
                      </TableCell>
                      <TableCell component="th" scope="row">
                        {prod.upc}
                      </TableCell>
                      <TableCell align="right">{''}</TableCell>
                      <TableCell align="right">{``}</TableCell>
                      <TableCell align="right">{``}</TableCell>
                      <TableCell align="right">{1}</TableCell>
                      <TableCell align="right">{``}</TableCell>
                      <TableCell align="right">{``}</TableCell>
                      <TableCell align="right">{prod.soldPrice.toFixed(2)}</TableCell>
                      <TableCell align="right">{prod.soldPrice.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        );
      }}
      onRowClick={(event, rowData, togglePanel) => togglePanel()}
      options={{
        actionsColumnIndex: -1,
        pageSize: parseInt(pagination),
        pageSizeOptions: [5, 10, 20, 50, 100, 200],
      }}
      actions={[
        {
          tooltip: 'Download CSV(Per Trans)',
          isFreeAction: true,
          icon: CloudDownload,
          onClick: (evt, rowData) => {
            console.log('rowData', evt);
            console.log('rd', rowData);
            let length = 0;
            let downloaddata = filteredData?.length === 0 ? [...order] : [...filteredData];
            const filteredArray = downloaddata.filter((e) => e.products.some((e) => !e.err));

            let outPutArray = filteredArray.map((e) => {
              if (e.products.length > length) length = e.products.length;
              let date = new Date(e.createdAt);
              let date1 = new Date(e.createdAt);
              if (e.realTime) {
                date = moment.utc(date).format('lll');
                date1 = moment.utc(date).format('MM/DD/YYYY');
              } else {
                date = moment(date).format('lll');
                date1 = moment(date).format('MM/DD/YYYY');
              }
              date = date.replaceAll(',', ' ');
              //replace description too, because it may contain comma
              if (e.machineInfo.description) {
                e.machineInfo.description = e.machineInfo.description.replaceAll(',', ';');
              }
              return {
                Order_ID: e.id,
                Order_Date: date,
                Order_Date_Only: date1,
                Machine_ID: e.machineInfo.name,
                Machine_Desc: e.machineInfo.description,
                Currency: e.currency,
                Tax: e.tax,
                Sale_Amount: e.total,
                Products: e.products,
                Email: e.email,
                One_Time_Code: e.otc,
                Subscripted: e.isCheck,
                Payment_type: e.payment_type,
                EXID: e.custom,
              };
            });

            let csv = '';

            let header = `Order_ID,Order_Date,Order_Date_Only,Machine_ID,Machine_Desc,Currency,Tax,Sale_Amount,Email,Email_Subscribed,Code_Used,Payment_Type,`;

            for (var i = 1; i <= length; i++) {
              header += `Product${i} Name,Product${i} UPC/SKU,Product${i} Cost Price(unit),Product${i} Cost Price(total),Product${i} Unit Price, Product${i} Quantity,Product${i} Sub Total without Tax,Product${i} Tax,Product${i} Promotion Used,Product${i} Code Sent,Product${i} Discount, `;
            }

            let values = outPutArray
              .map((o) => {
                let r = '';
                r +=
                  o.Order_ID +
                  ',' +
                  o.Order_Date +
                  ',' +
                  o.Order_Date_Only +
                  ',' +
                  o.Machine_ID +
                  ',' +
                  o.Machine_Desc +
                  ',' +
                  o.Currency +
                  ',' +
                  o.Tax +
                  ',' +
                  o.Sale_Amount +
                  ',';
                if (o.Email) r += o.Email + ',';
                else r += ' ,';

                if (o.Subscripted) r += o.Subscripted + ',';
                else r += ' ,';

                if (o.One_Time_Code) r += o.One_Time_Code + ',';
                else r += ' ,';

                if (o.payment_type) r += o.payment_type + ',';
                else r += ' ,';

                o.Products.forEach((element, idx) => {
                  if (!element.err) {
                    let i = idx + 1;
                    if (element.productInfo) {
                      const name = element.productInfo?.name?.replaceAll(',', ';');

                      r += name + ',';
                      r += element.productInfo.upc + ',';
                      r += element.productInfo.costPrice + ',';
                      r += element.quantity + ',';
                      r += element.productInfo.price + ',';
                    } else {
                      r += ' ,';
                      r += ' ,';
                      r += ' ,';
                      r += ' ,';
                    }

                    r += element.quantity + ',' + element.soldPrice + ',';
                    if (element.tax_amount) {
                      r += element.tax_amount + ',';
                    } else {
                      r += 0 + ',';
                    }
                    if (element.promoInfo)
                      r += element.promoInfo.title + '(' + element.promoInfo.promoType + ')' + ',';
                    else r += ' ,';
                    if (element.code) r += element.code + ',';
                    else r += ' ,';
                    r += element.discount + ' ,';
                  }
                });
                return r;
              })
              .join('\n');
            csv += header + '\n' + values;
            var blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            var blobUrl = URL.createObjectURL(blob);
            var link = document.createElement('a');
            link.setAttribute('href', blobUrl);
            link.setAttribute('download', 'my_transaction_data.csv');
            document.body.appendChild(link); // Required for FF

            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl);
          },
        },
        {
          tooltip: 'Download CSV(Per Item)',
          isFreeAction: true,
          icon: CloudDownload,
          onClick: (evt, data) => {
            let downloaddata = filteredData?.length === 0 ? [...order] : [...filteredData];
            const filteredArray = downloaddata;
            let length = 0;
            let outPutArray = [];
            filteredArray?.forEach((e) => {
              let date = new Date(e.createdAt);
              let date1 = new Date(e.createdAt);
              if (e.realTime) {
                date = moment.utc(date).format('lll');
                date1 = moment.utc(date).format('MM/DD/YYYY');
              } else {
                date = moment(date).format('lll');
                date1 = moment(date).format('MM/DD/YYYY');
              }
              date = date.replace(',', ' ');
              // console.log(order);
              for (var i = 0; i < e.products?.length; i++) {
                const thisData = {
                  Order_ID: e.id,
                  Order_Date: date,
                  Order_Date_Only: date1,
                  Machine_ID: e.machineInfo.name,
                  Machine_Desc: e.machineInfo.description,
                  Currency: e.currency,
                  Sale_Amount:
                    e.products[i].soldPrice +
                    (e.products[i].tax_amount ? e.products[i].tax_amount : 0),
                  Tax: e.products[i].tax_amount ? e.products[i].tax_amount : 0,
                  Email: e.email ? e.email : '',
                  Email_Subscribed: e.isCheck ? e.isCheck : '',
                  Code_Used: e.otc ? e.otc : '',
                  Payment_Type: e.payment_type ? e.payment_type : '',
                  EXID: e.custom ? e.custom : '',
                  Product_Name: e.products[i].productInfo?.name,
                  Product_UPC_SKU: e.products[i].productInfo?.upc,
                  Product_Cost_Price: e.products[i].productInfo?.costPrice,
                  Product_Unit_Price: e.products[i].productInfo?.price,
                  Product_Quantity: e.products[i].quantity,

                  Product_Promotion: e.products[i].promo
                    ? e.products[i].promo?.title + '(' + e.products[i].promo?.promoType + ')'
                    : '',
                  Product_Code_Sent: e.products[i].code ? e.products[i].code : '',
                  Product_Discount: e.products[i].discount,
                };

                thisData.Email_Subscribed = e.isCheck ? e.isCheck : '';

                if (!e.products[i].err) {
                  outPutArray.push(thisData);
                }
              }
            });

            const options = {
              fieldSeparator: ',',
              quoteStrings: '"',
              decimalSeparator: '.',
              useTextFile: false,
              filename: 'Sales Report CSV(per item)',
              useKeysAsHeaders: true,
            };
            console.log('outPutArray', outPutArray);
            const csvExporter = new ExportToCsv(options);
            csvExporter.generateCsv(outPutArray);
          },
        },
        {
          tooltip: 'Send Email Receipt',
          icon: Email,
          onClick: async (event, rowData) => {
            const { value: email } = await Swal.fire({
              title: 'Enter the email address',
              input: 'text',
              inputPlaceholder: 'Enter the email ("delete" for deleting email address)',
            });
            if (email) {
              Swal.fire({
                title: `Please Wait for response`,
                didOpen: () => {
                  Swal.showLoading();
                },
              });
              console.log('rowData', rowData);
              await request({
                method: 'post',
                url: '/receipt/manual',
                data: {
                  email: email,
                  orderID: rowData.id,
                },
              })
                .then(() => {
                  Swal.fire({
                    icon: 'success',
                    text: `The receipt has been send.`,
                  });
                })
                .catch(() => {
                  Swal.fire({
                    icon: 'error',
                    text: `Fail to send the receipt`,
                  });
                });
            }
          },
        },
      ]}
    />
  );
}
